import React, { forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import InputMask from 'react-input-mask';

import { documentMasks } from '../../../common/utils/document-masks';
import {
  useMpCardSectionCore,
  UseMpCardSectionArgs,
} from './useMpCardSectionCore';
import { MpCardSectionRef } from './mp-card-section';
import { Checkout } from '../../../common/types/checkout';
import { FormDataShape } from '../../../common/hooks/useCheckoutController/useCheckoutController.types';
import * as PM from '../../organisms/modern/payment-form/payment-form.organism.style';
import { cvvMaxForBrand } from '../../../common/utils/card-helpers';
import { encodeBase64Browser } from '../../../common/utils/base64.utils';

type Props = UseMpCardSectionArgs & {
  checkout: Checkout;
  data: FormDataShape;
};

const ModernInputWrap = styled(PM.InputWrap)`
  label {
    background: #fff;
    padding: 0 6px;
    z-index: 2;
    transition: all 0.15s ease;
    pointer-events: none;
  }

  input:not(:placeholder-shown) + label,
  input:focus + label,
  select:focus + label,
  select:not([value='']) + label {
    background: linear-gradient(to bottom, #F4F5F7 50%, #fff 50%);
  }

  &.always-top-label label {
    top: 0;
    transform: translateY(-50%) scale(0.85);
    font-size: 13px;
    background: linear-gradient(to bottom, #F4F5F7 50%, #fff 50%);
  }
`;

const MaskedInput = styled(InputMask)`
  width: 100%;
  height: 48px;
  border-radius: 10px;
  border: 1.5px solid #D9DDE3;
  padding: 0 14px;
  background: #fff;
  font-size: 15px;
  color: #18233A;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #b0b5bd;
  }

  &:focus {
    outline: 0;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(40, 121, 254, 0.1);
  }

  @media (max-width: 380px) {
    height: 44px;
    font-size: 14px;
    padding: 0 12px;
  }
`;

export const MpCardSectionModern = forwardRef<MpCardSectionRef, Props>(
  ({ checkout, data, onChange, payerEmail }, ref) => {
    const core = useMpCardSectionCore({ checkout, data, onChange, payerEmail });
    const {
      t,
      cardNumber,
      cardName,
      expiry,
      cvv,
      installments,
      installmentList,
      onChangeCardNumber,
      setCardName,
      onChangeExpiry,
      onChangeCvv,
      setInstallments,
      createToken,
      getPayload,
      brand,
    } = core;

    useImperativeHandle(ref, () => ({
      createToken,
      getPayload,
    }));

    const handleCvvChange = (value: string) => {
      const onlyDigits = value.replace(/\D/g, '');

      const max = cvvMaxForBrand(brand);

      const trimmed = onlyDigits.slice(0, max);

      onChangeCvv(trimmed);

      const b64 = encodeBase64Browser(trimmed);

      onChange('cvvCardId', b64);
      onChange('cardCvv', trimmed);
    };
    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <PM.CardGrid>
          <ModernInputWrap className="span-2">
            <input
              id="mp-modern-cardNumber"
              placeholder=" "
              inputMode="numeric"
              autoComplete="cc-number"
              value={cardNumber}
              onChange={(e) => onChangeCardNumber(e.target.value)}
              maxLength={23}
            />
            <label htmlFor="mp-modern-cardNumber">{t('cardNumber')}</label>
          </ModernInputWrap>

          <ModernInputWrap>
            <input
              id="mp-modern-cardName"
              placeholder=" "
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              autoComplete="cc-name"
            />
            <label htmlFor="mp-modern-cardName">{t('cardName')}</label>
          </ModernInputWrap>

          <ModernInputWrap>
            <input
              id="mp-modern-expiry"
              placeholder=" "
              inputMode="numeric"
              autoComplete="cc-exp"
              value={expiry}
              onChange={(e) => onChangeExpiry(e.target.value)}
              maxLength={5}
            />
            <label htmlFor="mp-modern-expiry">{t('expiryDate')}</label>
          </ModernInputWrap>

          <ModernInputWrap>
            <input
              id="mp-modern-cvv"
              placeholder=" "
              inputMode="numeric"
              autoComplete="cc-csc"
              value={cvv}
              onChange={(e) => handleCvvChange(e.target.value)}
              maxLength={cvvMaxForBrand(brand)}
            />
            <label htmlFor="mp-modern-cvv">CVV</label>
          </ModernInputWrap>

          <ModernInputWrap className="span-2">
            <MaskedInput
              id="mp-modern-cpf"
              mask={documentMasks[checkout?.paisEnum]}
              maskChar=""
              placeholder=" "
              value={data.cpf}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange('cpf', e.target.value)
              }
            />
            <label htmlFor="mp-modern-cpf">{t('document')}</label>
          </ModernInputWrap>

          <ModernInputWrap className="span-2 always-top-label">
            <select
              id="mp-modern-installments"
              value={String(installments)}
              onChange={(e) => setInstallments(Number(e.target.value))}
            >
              {installmentList.length === 0 ? (
                <option value="1"></option>
              ) : (
                installmentList.map((opt) => (
                  <option
                    key={opt.installments}
                    value={String(opt.installments)}
                  >
                    {opt.recommended_message}
                  </option>
                ))
              )}
            </select>
            <label htmlFor="mp-modern-installments">{t('installment')}</label>
          </ModernInputWrap>
        </PM.CardGrid>
      </form>
    );
  },
);
